import React, { useState } from 'react';

interface FilterBarProps {
  expanded: boolean;
  availableRepos: string[];
  availableBranches: string[];
  /** S-DM-001: branch is not a corpus-wide dimension — disabled with no repo selected. */
  branchDisabled?: boolean;
  /** Visible reason shown next to the control whenever it is disabled. */
  branchDisabledReason?: string;
  selectedRepo: string;
  selectedBranch: string;
  onApply: (repo: string, branch: string) => void;
  onClear: () => void;
}

export default function FilterBar({
  expanded,
  availableRepos,
  availableBranches,
  branchDisabled = false,
  branchDisabledReason = '',
  selectedRepo,
  selectedBranch,
  onApply,
  onClear,
}: FilterBarProps): React.ReactElement | null {
  const [localRepo, setLocalRepo] = useState(selectedRepo);
  const [localBranch, setLocalBranch] = useState(selectedBranch);

  // Sync local state when parent resets
  React.useEffect(() => {
    setLocalRepo(selectedRepo);
  }, [selectedRepo]);

  React.useEffect(() => {
    setLocalBranch(selectedBranch);
  }, [selectedBranch]);

  if (!expanded) return null;

  function handleApply(): void {
    onApply(localRepo, localBranch);
  }

  function handleClear(): void {
    setLocalRepo('');
    setLocalBranch('');
    onClear();
  }

  function handleDismissRepo(): void {
    setLocalRepo('');
    onApply('', localBranch);
  }

  function handleDismissBranch(): void {
    setLocalBranch('');
    onApply(localRepo, '');
  }

  const hasActiveChips = localRepo !== '' || localBranch !== '';

  return (
    <div className="filter-bar" data-testid="filter-bar">
      <select
        data-testid="repo-filter"
        className="filter-select"
        value={localRepo}
        onChange={(e) => setLocalRepo(e.target.value)}
        aria-label="Filter by repository"
      >
        <option value="">All repositories</option>
        {availableRepos.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
      <select
        data-testid="branch-filter"
        className="filter-select"
        value={localBranch}
        onChange={(e) => setLocalBranch(e.target.value)}
        aria-label="Filter by branch"
        disabled={branchDisabled}
        aria-describedby={branchDisabled && branchDisabledReason ? 'branch-filter-reason' : undefined}
        title={branchDisabled ? branchDisabledReason : undefined}
      >
        <option value="">All branches</option>
        {availableBranches.map((b) => (
          <option key={b} value={b}>{b}</option>
        ))}
      </select>
      {/* S-DM-001: a disabled control must say WHY, not just be inert. */}
      {branchDisabled && branchDisabledReason && (
        <span
          id="branch-filter-reason"
          className="filter-disabled-reason"
          data-testid="branch-filter-reason"
        >
          {branchDisabledReason}
        </span>
      )}
      <button type="button" className="filter-apply-btn" onClick={handleApply}>
        Apply
      </button>
      <button type="button" className="filter-clear-btn" onClick={handleClear}>
        Clear
      </button>

      {/* Active filter chips — immediate dismiss applies filter */}
      {hasActiveChips && (
        <div className="filter-chips" data-testid="filter-chips">
          {localRepo && (
            <span className="filter-chip" data-testid="filter-chip-repo">
              repo: {localRepo}
              <button
                type="button"
                className="filter-chip-dismiss"
                aria-label="Remove repo filter"
                onClick={handleDismissRepo}
              >
                ×
              </button>
            </span>
          )}
          {localBranch && (
            <span className="filter-chip" data-testid="filter-chip-branch">
              branch: {localBranch}
              <button
                type="button"
                className="filter-chip-dismiss"
                aria-label="Remove branch filter"
                onClick={handleDismissBranch}
              >
                ×
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
